import React, { useEffect, useRef, useState } from 'react';
import { coachingCreditsAPI } from '../services/api';
import styles from '../styles/CheckoutReturn.module.css';

/**
 * Handles the return from a coaching-credits Stripe Checkout
 * (?coaching_checkout=success|cancelled) — the sibling of CheckoutReturn
 * (subscriptions), sharing its stylesheet and shape.
 *
 * Recording is idempotent between the webhook and the pull-based
 * /sync-session; we drive the pull here so the credits appear even with no
 * webhook reachable (localhost, fresh deploys). Unlike a plan purchase, the
 * credits don't gate the whole app, so no full reload is needed: the CTA
 * cleans the URL and hands off to onDone (App navigates to the booking
 * screen, which fetches the fresh balance).
 *
 * Props:
 * - onDone: called after a successful purchase's CTA — go book the session.
 */
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

function CoachingCheckoutReturn({ onDone }) {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get('coaching_checkout');
  const sessionId = params.get('session_id');
  const [state, setState] = useState(outcome === 'success' ? 'waiting' : 'idle'); // waiting | done | slow | idle
  const [creditsBought, setCreditsBought] = useState(0);
  const startedAt = useRef(Date.now());

  // Cancelled (or no param): just make sure the URL is clean.
  useEffect(() => {
    if (outcome === 'cancelled') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [outcome]);

  // Pull the Checkout Session from Stripe via the server until it reads paid.
  useEffect(() => {
    if (state !== 'waiting' || !sessionId) return undefined;

    let stopped = false;
    let timer;
    const tick = async () => {
      try {
        const result = await coachingCreditsAPI.syncCheckoutSession(sessionId);
        if (!stopped && result?.ok) {
          setCreditsBought(result.credits || 0);
          setState('done');
          return;
        }
      } catch (e) { /* keep polling */ }
      if (stopped) return;
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setState('slow');
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => { stopped = true; clearTimeout(timer); };
  }, [state, sessionId]);

  if (outcome !== 'success' || state === 'idle') return null;

  const handleDone = () => {
    window.history.replaceState({}, document.title, window.location.pathname);
    if (onDone) onDone();
  };

  return (
    <div className={styles.screen}>
      <div className={styles.inner}>
        <div className={styles.eyebrow}>Sophia</div>
        <h1 className={styles.title}>Payment received</h1>
        {state === 'waiting' ? (
          <>
            <p className={styles.text}>Adding your coaching sessions…</p>
            <div className={styles.dots}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          </>
        ) : state === 'done' ? (
          <>
            <p className={styles.text}>
              {creditsBought === 1
                ? 'Your coaching session is ready to book.'
                : `${creditsBought} coaching sessions are ready to book.`}
            </p>
            <button className={styles.button} onClick={handleDone}>
              Book your session
            </button>
          </>
        ) : (
          <>
            <p className={styles.text}>
              Confirmation is taking a little longer than usual — your sessions
              will appear automatically in a few minutes. You don&apos;t need to
              pay again.
            </p>
            <button className={styles.button} onClick={handleDone}>
              Continue to Sophia
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default CoachingCheckoutReturn;
